import { useState } from "react";

import { getErrorMessage } from "@/core/services";
import { useAsyncList, useLastKnownTotal, useTableState } from "@/ds";
import { ESCOPOS_PADRAO_SORT_FIELDS, getEscoposPadrao } from "@/modules/financeiro-configuracoes";
import type {
  EscopoPadraoLinha,
  ListarEscoposPadraoParams,
  OrdenarEscoposPadraoPor,
  VisaoEscopoPadrao,
} from "@/modules/financeiro-configuracoes";
import {
  ESCOPOS_PADRAO_ERROR_MESSAGE,
  ESCOPOS_PADRAO_FILTRO_TODOS,
  ESCOPOS_PADRAO_FORBIDDEN_MESSAGE,
} from "../escopos-padrao.constants";

export interface FiltrosEscoposPadrao {
  tipoObraId: string;
  grupoId: string;
  embarcacaoId: string;
}

export const FILTROS_VAZIOS: FiltrosEscoposPadrao = {
  tipoObraId: ESCOPOS_PADRAO_FILTRO_TODOS,
  grupoId: ESCOPOS_PADRAO_FILTRO_TODOS,
  embarcacaoId: ESCOPOS_PADRAO_FILTRO_TODOS,
};

const isSortField = (value: unknown): value is OrdenarEscoposPadraoPor =>
  ESCOPOS_PADRAO_SORT_FIELDS.some((campo) => campo === value);

export function useEscoposPadraoLista(versao: string | null) {
  const [visao, setVisao] = useState<VisaoEscopoPadrao>("EQUIPAMENTOS");
  const [filtros, setFiltros] = useState<FiltrosEscoposPadrao>(FILTROS_VAZIOS);
  const [mostrarInativos, setMostrarInativos] = useState(false);

  const { lastTotal, remember } = useLastKnownTotal();
  const tabela = useTableState<OrdenarEscoposPadraoPor>({
    isSortField,
    total: lastTotal,
    resetKey: JSON.stringify([versao, visao, filtros, mostrarInativos]),
  });

  const lista = useAsyncList<ListarEscoposPadraoParams, EscopoPadraoLinha>({
    enabled: versao !== null,
    fetcher: (params, signal) => getEscoposPadrao(params, { signal }),
    params: {
      versao: versao ?? "",
      visao,
      tipoObraId: filtros.tipoObraId || undefined,
      grupoEscopoMacroId: filtros.grupoId || undefined,
      embarcacaoId: filtros.embarcacaoId || undefined,
      incluirInativos: mostrarInativos,
      page: tabela.page + 1,
      pageSize: tabela.rows,
      sort: tabela.sort ?? undefined,
    },
    forbiddenMessage: ESCOPOS_PADRAO_FORBIDDEN_MESSAGE,
    errorMessage: ESCOPOS_PADRAO_ERROR_MESSAGE,
    resolveErrorMessage: getErrorMessage,
  });
  remember(lista.status, lista.total);

  return {
    visao,
    setVisao,
    filtros,
    setFiltros,
    mostrarInativos,
    setMostrarInativos,
    tabela,
    items: lista.items,
    total: lista.total,
    status: lista.status,
    errorMessage: lista.errorMessage,
    reload: lista.reload,
  };
}
